import { Aide } from '@/components/ui/aide';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trucksAPI } from '../lib/api';
import {
  Search as SearchIcon,
  Truck,
  ArrowRight,
  MapPin,
  User,
  Building2,
  Clock,
  Loader2,
  X,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/ui/page-header';
import { EmptyState } from '../components/ui/empty-state';
import {
  cn,
  getStatusBadgeClass,
  formatRelativeTime,
  formatDate,
  getTripProgress,
} from '../lib/utils';
import { libelle, nomPays, texteSur, nomLisible, referenceLisible } from '../lib/libelles';
import { formatLitres } from '../lib/formats';

const SearchPage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingResult, setIsLoadingResult] = useState(false);

  // Debounced search for suggestions
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await trucksAPI.search(query);
        setSuggestions(results);
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleSelectTruck = async (matricule) => {
    setIsLoadingResult(true);
    setSelectedResult(null);
    setSuggestions([]);
    setQuery(matricule);

    try {
      const result = await trucksAPI.getByMatricule(matricule);
      setSelectedResult(result);
    } catch (err) {
      console.error('Failed to load truck details:', err);
    } finally {
      setIsLoadingResult(false);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSuggestions([]);
    setSelectedResult(null);
  };

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="search-page">
      <PageHeader
        eyebrow="Recherche"
        title="Rechercher"
        description="Trouvez un camion par son matricule."
      />

      {/* Search Input */}
      <div className="relative">
        <div className="relative">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground/70" />
          <Input
            type="text"
            placeholder="Entrez le matricule (ex: SN-2456-AA)"
            value={query}
            onChange={(e) => setQuery(e.target.value.toUpperCase())}
            className="input-base pl-12 pr-12 text-lg font-mono"
            data-testid="search-input"
          />
          {query && (
            <button
              onClick={clearSearch}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded"
            >
              <X className="w-5 h-5 text-muted-foreground/70" />
            </button>
          )}
        </div>

        {/* Suggestions Dropdown */}
        {suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-card border-2 border-border rounded-lg shadow-lg z-10 overflow-hidden">
            {suggestions.map((truck) => (
              <button
                key={truck.id}
                onClick={() => handleSelectTruck(truck.matricule)}
                className="w-full flex items-center justify-between p-4 hover:bg-muted/50 border-b last:border-b-0 transition-colors"
                data-testid={`suggestion-${truck.matricule}`}
              >
                <div className="flex items-center gap-3">
                  <div className="matricule-plate text-base">
                    {truck.matricule}
                  </div>
                  <span className="text-muted-foreground">
                    {formatLitres(truck.capacity_liters)}
                  </span>
                </div>
                <Badge
                  className={cn(
                    'status-badge',
                    truck.current_status === 'FREE'
                      ? 'bg-success/15 text-success'
                      : truck.current_status === 'ON_TRIP'
                      ? 'bg-flame/15 text-flame-ink'
                      : 'bg-flame/15 text-flame-ink'
                  )}
                >
                  {libelle('truckStatus', truck.current_status)}
                </Badge>
              </button>
            ))}
          </div>
        )}

        {/* Loading indicator */}
        {isSearching && (
          <div className="absolute top-full left-0 right-0 mt-2 p-4 bg-card border-2 border-border rounded-lg shadow-lg text-center">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-flame" />
          </div>
        )}
      </div>

      {/* Loading result */}
      {isLoadingResult && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-flame" />
        </div>
      )}

      {/* Search Result */}
      {selectedResult && (
        <div className="space-y-6" data-testid="search-result">
          {/* Truck Info */}
          <Card className="card-base">
            <CardContent className="p-3">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-flame/10 rounded-xl flex items-center justify-center">
                    <Truck className="w-8 h-8 text-flame" />
                  </div>
                  <div>
                    <div className="matricule-plate">
                      {selectedResult.truck.matricule}
                    </div>
                    <p className="text-muted-foreground mt-1">
                      {formatLitres(selectedResult.truck.capacity_liters)}
                    </p>
                  </div>
                </div>
                <Badge
                  className={cn(
                    'status-badge text-sm px-4 py-2',
                    selectedResult.truck.current_status === 'FREE'
                      ? 'bg-success/15 text-success border-success/40'
                      : selectedResult.truck.current_status === 'ON_TRIP'
                      ? 'bg-flame/15 text-flame-ink border-flame/40'
                      : 'bg-flame/15 text-flame-ink border-flame/40'
                  )}
                >
                  {libelle('truckStatus', selectedResult.truck.current_status)}
                </Badge>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="readout-label">
                    Capacité
                  </p>
                  <p className="font-bold">
                    {formatLitres(selectedResult.truck.capacity_liters)}
                  </p>
                </div>
                <div>
                  <p className="readout-label">
                    État technique
                  </p>
                  <p className="font-bold">
                    {libelle('technicalStatus', selectedResult.truck.technical_status, 'En bon état')}
                  </p>
                </div>
                <div>
                  <p className="readout-label">
                    Statut
                  </p>
                  <p className="font-bold">
                    {libelle('truckStatus', selectedResult.truck.current_status)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current Trip */}
          {selectedResult.current_trip ? (
            <Card className="card-base border-2 border-flame">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-4">
                  <h3>Trajet en cours</h3>
                  <Badge
                    className={cn(
                      'status-badge',
                      getStatusBadgeClass(selectedResult.current_trip.status)
                    )}
                  >
                    {libelle('tripStatus', selectedResult.current_trip.status)}
                  </Badge>
                </div>

                <div className="flex items-center gap-3 text-lg mb-4">
                  <span className="font-bold">
                    {nomPays(selectedResult.current_trip.departure_country)}
                  </span>
                  <ArrowRight className="w-5 h-5 text-muted-foreground/70" />
                  <span className="font-bold">
                    {nomPays(selectedResult.current_trip.destination_country)}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="mb-4">
                  <div className="trip-progress h-3">
                    <div
                      className="trip-progress-bar"
                      style={{
                        width: `${getTripProgress(
                          selectedResult.current_trip.status
                        )}%`,
                      }}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {getTripProgress(selectedResult.current_trip.status)} % du trajet fait
                  </p>
                </div>

                {/* Latest update */}
                {selectedResult.latest_update && (
                  <div className="bg-muted/50 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-flame mt-0.5" />
                      <div>
                        <p className="font-medium">
                          {texteSur(selectedResult.latest_update.location_text, 'Position non précisée')}
                        </p>
                        {selectedResult.latest_update.customs_checkpoint && (
                          <p className="text-sm text-flame-ink font-medium">
                            Poste douanier:{' '}
                            {texteSur(selectedResult.latest_update.customs_checkpoint)}
                          </p>
                        )}
                        {selectedResult.latest_update.note && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {texteSur(selectedResult.latest_update.note, '')}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground/70 mt-2 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatRelativeTime(
                            selectedResult.latest_update.created_at
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Driver & Operation */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedResult.driver && (
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <User className="w-5 h-5 text-muted-foreground/70" />
                      <div>
                        <p className="readout-label">
                          Chauffeur
                        </p>
                        <p className="font-medium">
                          {nomLisible(selectedResult.driver.full_name)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {selectedResult.driver.phone}
                        </p>
                      </div>
                    </div>
                  )}
                  {selectedResult.operation && (
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <Building2 className="w-5 h-5 text-muted-foreground/70" />
                      <div>
                        <p className="readout-label">
                          Convoi
                        </p>
                        <p className="font-medium">
                          {nomLisible(selectedResult.operation.beneficiary_name)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {[referenceLisible(selectedResult.operation.code, ''), nomPays(selectedResult.operation.country, '')]
                            .filter(Boolean)
                            .join(' • ')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <Button
                  className="w-full mt-4 btn-primary"
                  onClick={() =>
                    navigate(`/trips/${selectedResult.current_trip.id}`)
                  }
                  data-testid="view-trip-btn"
                >
                  Voir les détails du trajet
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="card-base bg-muted/50">
              <CardContent className="p-3 text-center">
                <p className="flex items-center justify-center gap-1 text-muted-foreground">
                  Ce camion n’a pas de trajet en cours.
                  <Aide titre="Camion disponible">
                    Il est disponible pour être affecté à une commande.
                  </Aide>
                </p>
              </CardContent>
            </Card>
          )}

          {/* Trip History */}
          {(!selectedResult.trip_history ||
            selectedResult.trip_history.length === 0) && (
            <Card className="card-base bg-muted/50">
              <CardContent className="p-3 text-center">
                <p className="flex items-center justify-center gap-1 text-muted-foreground">
                  Aucun trajet terminé à l’historique de ce camion.
                  <Aide titre="Historique des trajets">
                    Chaque trajet clôturé viendra s’ajouter ici.
                  </Aide>
                </p>
              </CardContent>
            </Card>
          )}

          {selectedResult.trip_history &&
            selectedResult.trip_history.length > 0 && (
              <div>
                <h3 className="mb-4">
                  Historique des trajets
                </h3>
                <div className="space-y-3">
                  {selectedResult.trip_history.map((trip) => (
                    <Card
                      key={trip.id}
                      className="card-base card-interactive"
                      onClick={() => navigate(`/trips/${trip.id}`)}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="text-sm">
                              <span className="font-medium">
                                {nomPays(trip.departure_country)}
                              </span>
                              <ArrowRight className="w-4 h-4 inline mx-2 text-muted-foreground/70" />
                              <span className="font-medium">
                                {nomPays(trip.destination_country)}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge
                              className={cn(
                                'status-badge text-xs',
                                getStatusBadgeClass(trip.status)
                              )}
                            >
                              {libelle('tripStatus', trip.status)}
                            </Badge>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDate(trip.departure_date)}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
        </div>
      )}

      {/* Avant, cet écran ne disait « entrez 2 caractères » qu'au repos et
          n'affichait plus rien du tout dès qu'une recherche ne trouvait
          personne : l'exploitant restait devant une page blanche sans savoir
          si le camion n'existait pas ou si l'écran était cassé. */}
      {!selectedResult && !isLoadingResult && query.length < 2 && (
        <EmptyState
          icon={SearchIcon}
          title="Retrouver un camion en une saisie"
          description="Tapez le matricule d’un camion pour voir d’un coup où il est, qui le conduit, sur quel trajet et avec quel convoi. Deux caractères suffisent pour commencer."
        />
      )}

      {!selectedResult &&
        !isLoadingResult &&
        !isSearching &&
        query.length >= 2 &&
        suggestions.length === 0 && (
          <EmptyState
            variant="filtered"
            title={`Aucun camion au matricule « ${query} »`}
            description="Vérifiez la saisie, ou enregistrez ce camion dans le parc s’il vient d’arriver dans la flotte."
            // Le camion cherché n'existe pas encore: le geste utile est de le
            // créer, pas de recommencer la même recherche.
            actionLabel="Ouvrir le parc"
            onAction={() => navigate('/trucks')}
          />
        )}
    </div>
  );
};

export default SearchPage;
